import { ProviderContext } from 'notistack';
import React from 'react';
import { useHistory } from "react-router-dom";
import { permisionURL, ResultObj } from './Domain';

export enum SnackbarColor {
    warning = "warning",
    success = "success"
}

/**
 * DDMenu 取得的內容
 */
export type DDMenuItem = {
    sps_id: string,
    name: string,
    remark: string
}

/**
 * 交車室DDMenu 取得的內容
 */
export type MeetingRoomDDMenuItem = {
    type: string
    children: Array<{ mr_id: string, name: string, remark: string }>
}

/**
 * 縣市區 DDMenu 取得的內容
 */
export type CityDDMenuItem = {
    name: string
    children: Array<{ name: string, code: string }>
}


/**
 * 單一頁面的權限資訊
 */
export type permissionPageInfo = {
    name: string
    code: string
    ctrl_code: string
    children: Array<permissionPageInfo>
    [key: string]: any
}

/**
 * 取得權限之後的格式
 */
export type PermissionItem = {
    items: Array<permissionPageInfo>
    sidebar: permissionPageInfo | null
    nav: permissionPageInfo | null
}

/**
 * 查詢頁面權限參數
 */
export type nowPageStateParam = {
    sidebar: permissionPageInfo | null
    groupCode: string
    pageCode: string | undefined
}

/**
 * 查詢頁面權限回傳參數
 */
export type nowPageStateResult = {
    groupArr: Array<permissionPageInfo>
    groupY: boolean
    pageArr: Array<permissionPageInfo>
    pageY: boolean
}

/**
 * table產生tr資料格式
 */
export type RowData<T> = {
    key: string // tr 跑回圈必須要有的
    data: T & {
        collapseComponent?: JSX.Element;
    }
    hasHover?: boolean;
    extroProps?: { [key: string]: any } // 額外放在TableRow的屬性
    [key: string]: any
};

/**
 * List產生ListItem資料格式
 */
export type ListItemData<T> = {
    key: string // List 跑回圈必須要有的
    data: T
    extroProps?: { [key: string]: any } // 額外放在ListItem的屬性
    [key: string]: any
};


/**
 * Tabs資料格式
 */
export type TabsData = {
    name: string;
    tabState?: number | undefined;
    extroProps?: { [key: string]: any } // 額外放在Tab底下Box的屬性
    [key: string]: any
};

export type PropHistory = {
    history: ReturnType<typeof useHistory>
}

/**
 * typescript Partial type
 * 將ProviderContext 的屬性變成可選擇性的給
 */
export type PartialProviderContext = Partial<ProviderContext>;

/**
 * 為了使用withSnakebar定義的Props
 * @description 使用& 來做交集型別(Intersection)
 * @param {*} enqueueSnackbar
 * @param {*} closeSnackbar
 */
export type PageProps = ProviderContext & PropHistory;

export enum ConfirmDialogPropsSize {
    LG = 'lg',
    MD = 'md',
    SM = 'sm',
    XL = 'xl',
    XS = 'xs'
}

/**
 * BODY modal整個上下捲動
 * PAPER 卷軸會被限縮在modal內 (請斟酌使用)
 */
export enum ConfirmDialogPropsScroll {
    BODY = 'body',
    PAPER = 'paper'
}

/**
 * 設定 overflow 用的
 */
export enum OverFlowOption {
    visible = "visible",
    hidden = "hidden",
    scroll = "scroll",
    auto = "auto",
}
/**
 * dialog modal 的modal內容
 */
export type ConfirmDialogProps = {
    id?: string,
    size: ConfirmDialogPropsSize,
    scroll: ConfirmDialogPropsScroll,
    modaltitle: string
    messages: any
    modalAction?: Function
    modalBtnGroup?: React.ReactNode | null
};


/**
 * 定義每個組件的參數型別
 */
export type PageState = {
    isModalLoaded?: boolean
    modal: ConfirmDialogProps | null
    modalOpen: boolean
    isLoaded: boolean
    openDialog: boolean
    dialogInfo: { message: string, data: any, modalOpen?: boolean } | null
    idb?: any | null,
    isSearchLoaded?: boolean
    isAPIPending?: boolean //是否有API正在等待處理，用於API debounce，判斷畫面上是否要出現loading UI
}

/**
 * TableBodyRow Props 類型
 */
export type TableBodyRowProps = {
    resData: Array<{ [key: string]: any }>
    btnPermisionGroup: { [key: string]: boolean }
} & PropHistory & PartialProviderContext;

/**
 * 事件
 */
export type HandleEvent = {
    target: {
        id: string
        name: string,
        value: any,
        type: string,
        key?: string
        checked?: boolean,
        files?: File[]
    }
} & Event


/**
 * 車型規格及內外裝 CarTypeXPrice 取得的內容
 */
export type CarTypeXPriceResItem = {
    car_type: string,
    car_type_name: string,
    company_rule: string,
    ct_id: string,
    Format: {
        car_type_price: string,
        ct_id: string,
        ctf_id: string,
        ctfp_id: string,
        format_code: string,
        format_name: string,
        invalid_date: string,
        valid_date: string,
        year: string,
        year_age: string,
        _car_type_price: string,
    }[],
    Exterior: {
        ce_id: string,
        ctep_id: string,
        ctf_id: string,
        exterior_code: string,
        exterior_color: string,
        exterior_price: string,
        format_code: string,
        invalid_date: string,
        valid_date: string,
        _exterior_price: string,
    }[],
    Interior: {
        ci_id: string,
        ctf_id: string,
        ctip_id: string,
        format_code: string,
        interior_code: string,
        interior_color: string,
        interior_price: string,
        invalid_date: string,
        valid_date: string,
        _interior_price: string,
    }[],
}

/** @description 審核JSON格式*/
export type auditJSON = {
    audit_breakpoint_level: number;
    audit_time: string;
    is_audit_breakpoint: boolean;
    leave_special_userlevel: boolean;
    level: number;
    note: string;
    status: string;
    ul_code: string;
    ul_id: string;
    ul_name: string;
    user_id: string;
}

/** @description 人事出勤共用搜尋 key */
export enum searchSettings {
    user_ids = 'user_ids',
    company_id = 'company_id',
    general_manager_id = 'general_manager_id',
    department_id = 'department_id',
    position_id = 'position_id',
    class_id = 'class_id',
    group_id = 'group_id',
    office_id = 'office_id',
}

export type SearchPage = hasPages | noPages;

type hasPages = {
    page: number;
    num_per_page: number;
}

type noPages = {
    page?: number;
    num_per_page?: number;
}

/**@description 審核資料 */
export type AuditItem = {
    audit_procedure_id: number,
    cre_time: string,
    id: string,
    note: string,
    status: string,
    type: string,
    type_name?: string,
    username: string,
    ul_name: string,
}
/**
 * 凱桃客製化簽核的VAC配件的型態定義
 */
export type VACComponent<T = { [key: string]: any }> = {
    type: "create" | "info" | "update" | "delete"
    data: T
}

/** 給response 的function用的 */
export type ResponseMessageType = (res: ResultObj, persist?: boolean) => void;

/** 定義只能丟YN的型別 */
export type TypeYN = "" | "Y" | "N"

/** 發票正規檢查 */
export const invoicesFormat = /^[A-Z]{2}[0-9]{8}$/

/** 美容預約狀態 */
export enum CosmeticVenueStatusEnum {
    /** 預約完成 */
    RESERVED = '預約完成',
    /** 預約確認 */
    CONFIRM = '預約確認',
    /** 施作中 */
    IMPLEMENT = '施作中',
    /** 施作完成 */
    COMPLETE = '施作完成',
    /** 未進廠 */
    NOTENTERED = '未進廠',
}

/**
 * OrderReview 分頁回傳訂單資訊給 PDI 申請作業的訊息型別
 * @description 送出端：OrderList/OrderReview（pageFrom=PDIApplyFirst）；接收端：PDIApply/ModalAddInner
 */
export const IMPORT_ORDER_MESSAGE_TYPE = "PDIApply/ImportOrder";

/** 問券類型 */
export enum QuestionTypeEnum {
    TRUEORFALSE = '是非',
    MULTIPLECHOICE = '單選',
    MULTIPLECHOICEMORE = '多選',
    CLOZE = '填空',
    LINEARSCALE = '線性刻度',
}